import { forwardRef } from 'react'
import { SlotActionButtons } from './SlotActionButtons'
import type { VolunteerSlot, SlotStatus } from '../../types/slot'
import {
  formatDisplayDate,
  formatDayWithPrefix,
  maskEmail,
  removeClassPrefix,
  isToday,
  isCurrentOrFutureDate
} from '@/utils/slotHelpers'

interface SlotTableRowProps {
  slot: VolunteerSlot
  index: number
  isFirstFutureDate: boolean
  status: SlotStatus
  canDelete: boolean
  userEmail: string | null
  getDayInfo: (dateStr: string) => any
  onRegister: (slotId: string) => void
  onDelete: (slotId: string) => void
  onAddToCalendar: (slot: VolunteerSlot) => void
}

export const SlotTableRow = forwardRef<HTMLTableRowElement, SlotTableRowProps>(
  function SlotTableRow(
    {
      slot,
      index,
      isFirstFutureDate,
      status,
      canDelete,
      userEmail,
      getDayInfo,
      onRegister,
      onDelete,
      onAddToCalendar
    },
    ref
  ) {
    const dayInfo = getDayInfo(slot.date)
    const holidayInfo = dayInfo?.holiday
    const campInfo = dayInfo?.camp
    const isHolidayOrBreak = holidayInfo && (holidayInfo.type === 'holiday' || holidayInfo.type === 'break')
    const dayString = slot.day?.trim().toLowerCase() || ''
    const isShabbat = dayString.includes('שבת') || new Date(slot.date).getDay() === 6
    const isTodaySlot = isToday(slot.date)
    const isMyRegistration = slot.volunteer_email === userEmail
    const isPastDate = !isCurrentOrFutureDate(slot.date)

    return (
      <tr
        ref={isFirstFutureDate ? ref : null}
        className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/20 transition-all duration-200 ${
          isTodaySlot
            ? 'bg-gradient-to-r from-blue-100 to-cyan-100 border-l-4 border-l-blue-500'
            : index % 2 === 0
            ? 'bg-white'
            : 'bg-gray-50/30'
        }`}
      >
        <td className="px-4 py-3">
          <div className="font-hebrew">
            <div className="font-bold text-gray-900 text-sm md:text-base">
              {slot.displayDate || formatDisplayDate(slot.date)}
            </div>
            <div className="text-xs md:text-sm text-gray-600 font-medium">
              {formatDayWithPrefix(slot.day)}
            </div>

            {(() => {
              const tags = dayInfo?.tags || []

              if (isShabbat && tags.length === 0) {
                tags.push({
                  name: 'יום מנוחה',
                  type: 'saturday',
                  color: 'bg-purple-100 text-purple-700 border-purple-200'
                })
              }

              if (tags.length === 0) return null

              return (
                <div className="space-y-1 mt-1">
                  {tags.map((tag: { name: string; type: string; color: string }, tagIndex: number) => (
                    <div
                      key={tagIndex}
                      className={`text-xs px-2 py-1 rounded-full inline-block font-medium border ${tag.color}`}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          {(() => {
            if ((isHolidayOrBreak || isShabbat) && !campInfo) {
              return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-800 font-hebrew border border-blue-200">
                  ללא
                </span>
              )
            }

            return (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-bold font-hebrew border ${
                  campInfo && (!slot.class_name || slot.class_name === 'פתוח')
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : 'bg-blue-100 text-blue-800 border-blue-200'
                }`}
              >
                {slot.class_name ? removeClassPrefix(slot.class_name) : 'פתוח'}
              </span>
            )
          })()}
        </td>

        <td className="px-4 py-3 text-gray-700 font-hebrew font-medium">
          {slot.volunteer_name || <span className="text-gray-400 italic">פנוי</span>}
        </td>

        <td className="px-4 py-3 text-gray-600 text-sm font-medium hidden sm:table-cell">
          {slot.volunteer_email ? maskEmail(slot.volunteer_email) : <span className="text-gray-400">-</span>}
        </td>

        <td className="px-4 py-3 text-start">
          <SlotActionButtons
            slotId={slot.id}
            volunteerName={slot.volunteer_name}
            volunteerEmail={slot.volunteer_email}
            className={slot.class_name}
            status={status}
            isMyRegistration={isMyRegistration}
            canDelete={canDelete}
            isHolidayOrBreak={isHolidayOrBreak}
            isShabbat={isShabbat}
            isCampDay={!!campInfo}
            isPastDate={isPastDate}
            onRegister={onRegister}
            onDelete={onDelete}
            onAddToCalendar={() => onAddToCalendar(slot)}
          />
        </td>
      </tr>
    )
  }
)
